import {
  Component,
  Input,
  Renderer2,
  TemplateRef,
  inject,
  ContentChild
} from '@angular/core';
import {CommonGeneralConfigService, CommonTokenStorage, MakeCallService} from 'common-framework';
import {ReplaceableDirective, ReplaceableComponentBase, ReplaceableComponent} from 'common-framework';
import * as bootstrap from 'bootstrap';

export interface TooltipConfigs {
  containerWidth?: string;
};

@Component({
  selector: 'foo-tooltip',
  templateUrl: './foo-tooltip.component.html',
  styleUrls: ['./foo-tooltip.component.scss'],
  hostDirectives: [ReplaceableDirective],
  providers: [{
    provide: ReplaceableComponentBase,
    useExisting: FooTooltipComponent
  }]
})
@ReplaceableComponent()
export class FooTooltipComponent {
  @ContentChild('htmlValue', {read: TemplateRef}) htmlValueRef: TemplateRef<any>;
  @Input() dataShimmer;
  @Input() isInDynamicListing = false;
  @Input() tooltipConfigs: TooltipConfigs = {};
  @Input() tooltipOptions: bootstrap.Tooltip.Options = {};
  curLang: string;

  protected _tooltipInstances: any[] = [];
  protected renderer: Renderer2 = inject(Renderer2);
  protected tokenStorage: CommonTokenStorage = inject(CommonTokenStorage);
  protected makeCall: MakeCallService = inject(MakeCallService);
  protected generalConfigService: CommonGeneralConfigService = inject(CommonGeneralConfigService);

  constructor() {
    if (this['isComponentReplaced']) return;
    this.curLang = this.tokenStorage.getCurrentLanguage();
  }

  ngOnInit(): void {
    if (this.dataShimmer === undefined) {
      this.makeCall.isLoading.subscribe((isLoading) => {
        this.dataShimmer = isLoading;
      });
    }
  }

  get isActivated() {
    return this.generalConfigService.getConfigProperty('fooTooltipConfig')?.active ?? true;
  }

  get containerWidth() {
    return this.tooltipConfigs?.['containerWidth'];
  }

  get tooltipInstances(): any[] {
    return this._tooltipInstances;
  }

  set tooltipInstances(instances: any[]) {
    this._tooltipInstances = instances;
  }

  getTooltipInstance(element: Element): any {
    return bootstrap.Tooltip.getInstance(element);
  }

  /**
   * Determines if the text inside an HTML element is visually truncated with ellipsis.
   * @param element - The element containing the text to check
   * @param containerElement - The container element that may constrain the text
   * @returns True if the text is truncated with ellipsis, false otherwise
   */
  isTextTrimmed(element: HTMLElement, containerElement): boolean {
    const text = element.textContent?.trim() || '';
    if (!text) return false;

    const computedStyle = window.getComputedStyle(element);
    const font = computedStyle.font;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    context.font = font;
    const textWidth: number = Math.ceil(context.measureText(text).width) - 1;

    const containerEl: HTMLElement = containerElement || element;

    const containerWidth: number = containerEl.clientWidth;
    const elementWidth: number = element.clientWidth;
    const scrollWidth: number = element.scrollWidth;
    const offsetWidth: number = element.offsetWidth;

    // Compare measured text width against available width in the container
    const isOverflowing: boolean = textWidth >= containerWidth;

    // Also consider scrollWidth vs offsetWidth as fallback logic
    const hasScrollClipping: boolean = scrollWidth > offsetWidth;

    return isOverflowing || hasScrollClipping;
  }

  /**
   * Gets the element that contains the tooltip text.
   * @param el - The parent element to search within
   * @returns The element containing the tooltip text or undefined if not found
   */
  getTooltipTextElement(el: HTMLElement): HTMLElement {
    let elements: NodeListOf<HTMLElement> = el.querySelectorAll('.foo-tooltip-text');
    if (elements.length === 0 && !this.isInDynamicListing) {
      elements = el.querySelectorAll('.foo-tooltip-element');
    }
    let element: HTMLElement;
    if (elements.length !== 0) {
      const elArray: HTMLElement[] = Array.from(elements);
      element = elArray[0];
    }
    return element;
  }

  /**
   * Shows a tooltip for the element.
   * Disposes any existing tooltips first.
   * @param el - The element to show the tooltip for
   */
  showTitleTooltip(el: HTMLElement): void {
    this.disposeAllTooltips();
    const defaultPlacement = this.tooltipOptions?.placement ?? (
      this.curLang.includes('ar') ? 'left' : 'right'
    );

    const element: HTMLElement = this.getTooltipTextElement(el);

    if (element) {
      const containerElement: HTMLElement = element.closest('.foo-tooltip-container') as HTMLElement;
      const dialogContainerElement: HTMLElement = element.closest('mat-dialog-container') as HTMLElement;

      const options: bootstrap.Tooltip.Options = {
        ...this.tooltipOptions,
        placement: defaultPlacement,
        title: this.tooltipOptions.title ?? element.innerText.trim(),
        container: this.tooltipOptions.container ?? dialogContainerElement ? 'mat-dialog-container' : false,
      };

      if (containerElement) {
        const elementWidth: number = element.offsetWidth;
        const containerWidth: number = containerElement.offsetWidth;

        if (containerWidth < elementWidth) {
          element.style.width = `${containerWidth}px`;
        }
      }

      let tooltipInstance = this.getTooltipInstance(element);

      const isTextTrimmed: boolean = this.isTextTrimmed(element, containerElement);

      if (!this.tooltipOptions.title) {
        if (isTextTrimmed) {
          this.checkDynamicListingStatus(element);
          if (!tooltipInstance) {
            tooltipInstance = new bootstrap.Tooltip(element, options);

            tooltipInstance.show();
            this.tooltipInstances.push(tooltipInstance);
          }
        }
      } else {
        if (!tooltipInstance) {
          tooltipInstance = new bootstrap.Tooltip(element, options);

          tooltipInstance.show();
          this.tooltipInstances.push(tooltipInstance);
        }
      }
    }
  }

  /**
   * Hides and disposes the tooltip for a given element.
   * @param el - The element to hide the tooltip for
   */
  hideTitleTooltip(el: HTMLElement): void {
    const element: HTMLElement = this.getTooltipTextElement(el);
    if (element) {
      let tooltipInstance = bootstrap.Tooltip.getInstance(element);

      if (tooltipInstance) {
        tooltipInstance.dispose();
        const index = this.tooltipInstances.indexOf(tooltipInstance);
        if (index !== -1) {
          this.tooltipInstances.splice(index, 1);
        }
      }
    }
  }

  /**
   * Retrieves the status class from an element or its ancestors.
   * Looks for classes that start with 'foo-status-'.
   * @param element - The element to check for status classes
   * @returns The status class string or null if none found
   */
  getFooStatusClass(element: HTMLElement): string | null {
    let currentElement: HTMLElement | null = element;

    while (currentElement) {
      const classList = Array.from(currentElement.classList);
      const match = classList.find(cls => cls.startsWith('foo-status-'));

      if (match) {
        return match; // e.g., "foo-status-success"
      }

      currentElement = currentElement.parentElement;
    }

    return null;
  }

  /**
   * Checks and applies dynamic styling to tooltips based on the element's status.
   * @param element - The element to check for status classes
   */
  checkDynamicListingStatus(element: HTMLElement): void {
    let bgColor;
    let textColor = "#fff";
    const statusClass = this.tooltipOptions?.customClass ? null : this.getFooStatusClass(element) || null;

    if (statusClass) {
      const elementCss = window.getComputedStyle(element);
      const statusBackgroundColor = elementCss.getPropertyValue(`background-color`);
      bgColor = statusBackgroundColor;

      const statusTextColor = elementCss.getPropertyValue('color');
      bgColor = statusTextColor;

      this.setTooltipStyles(bgColor, textColor);
    }
  }

  /**
   * Sets custom styles for tooltips by injecting a style element into the document head.
   * @param bgColor - The background color for the tooltip
   * @param textColor - The text color for the tooltip
   */
  setTooltipStyles(bgColor: string, textColor: string) {
    const existingStyle = document.head.querySelector('style.tooltip-style');

    if (existingStyle) {
      this.renderer.removeChild(document.head, existingStyle);
    }

    if (bgColor && textColor) {
      const style = this.renderer.createElement('style');
      style.className = 'tooltip-style';

      this.renderer.appendChild(document.head, style);
      const styleContent = `
            body .tooltip {
              --bs-tooltip-bg: ${bgColor};
              --bs-tooltip-color: ${textColor};
            }
          `;
      this.renderer.setProperty(style, 'textContent', styleContent);
    }
  }

  /**
   * Disposes all active tooltip instances and removes custom styles.
   */
  disposeAllTooltips(): void {
    this.setTooltipStyles('', '');
    this.tooltipInstances.forEach((instance) => {
      if (instance) {
        instance.dispose();
      }
    });
    this.tooltipInstances = [];
  }

  /**
   * Lifecycle hook that cleans up resources when the component is destroyed.
   * Disposes all tooltip instances.
   */
  ngOnDestroy(): void {
    this.disposeAllTooltips();
  }
}
